/**
 * BU Revenue Split — Inline
 *
 * Compact inline representation of Receita Bruta split between Business Units
 * (Digital / Promo), designed to live inside another card's header.
 */

import { useMemo } from 'react'
import { trpc } from '@/api/trpc'

const BU_COLORS: Record<string, string> = {
  Digital: '#00BCD4', // cyan
  Promo: '#FF9800',   // orange
}

interface BURevenueSplitInlineProps {
  startDate: string
  endDate: string
}

export function BURevenueSplitInline({ startDate, endDate }: BURevenueSplitInlineProps) {
  const { data } = trpc.dre.getBUMatrix.useQuery(
    {
      dataInicial: new Date(startDate),
      dataFinal: new Date(endDate),
    },
    {
      enabled: !!startDate && !!endDate,
      retry: false,
      staleTime: 5 * 60 * 1000,
    }
  )

  const slices = useMemo(() => {
    const businessUnits = data?.businessUnits ?? []
    const total = businessUnits.reduce((sum, bu) => sum + (bu.receita ?? 0), 0)
    if (total <= 0) return []
    return businessUnits
      .map((bu) => {
        const name = bu.businessUnit ?? ''
        const receita = bu.receita ?? 0
        return {
          name,
          percent: (receita / total) * 100,
          color: BU_COLORS[name] ?? '#9CA3AF',
        }
      })
      .sort((a, b) => b.percent - a.percent)
  }, [data])

  if (slices.length === 0) return null

  return (
    <div className="flex items-center gap-2 text-[10px] text-brand-gray uppercase tracking-wide whitespace-nowrap">
      {slices.map((s, idx) => (
        <span key={s.name} className="flex items-center gap-1">
          {idx > 0 && <span className="text-brand-gray/40 mr-1">|</span>}
          <span className="font-semibold text-white">{s.percent.toFixed(0)}%</span>
          <span>{s.name}</span>
        </span>
      ))}
    </div>
  )
}
